// TeamLogoCropper.js — frame a team logo in a circle before it uploads
// (2026-10-19). Every place a team logo shows is a circle (TeamLogo here,
// iOS's TeamMark, the PlayerWiki card behind the player), and the server's
// cut-out of the logo's own shape was clean for some uploads and ragged for
// others — so the coach frames the circle themselves. The square under it
// uploads as a 512 px JPEG, the same thing PW Manager's LogoCropSheet sends.
// Reuses HeaderImageCropper's modal styling; react-easy-crop does the work.
import { useCallback, useEffect, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import "../business/HeaderImageCropper.css";

const OUTPUT_SIDE = 512;

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Image failed to load"));
    img.src = src;
  });
}

// `area` is in image pixels. White under the logo: JPEG has no alpha, and a
// transparent PNG would otherwise come out on black.
async function squareJpeg(imageSrc, area) {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = OUTPUT_SIDE;
  canvas.height = OUTPUT_SIDE;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, OUTPUT_SIDE, OUTPUT_SIDE);
  ctx.drawImage(image, area.x, area.y, area.width, area.height, 0, 0, OUTPUT_SIDE, OUTPUT_SIDE);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Couldn't prepare the logo"))),
      "image/jpeg",
      0.9
    );
  });
}

export default function TeamLogoCropper({ imageFile, onCancel, onCrop }) {
  const [imageDataUrl, setImageDataUrl] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const areaRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    if (!imageFile) {
      setImageDataUrl(null);
      return;
    }
    fileToDataUrl(imageFile)
      .then((url) => { if (!cancelled) setImageDataUrl(url); })
      .catch((err) => { if (!cancelled) setError(err.message || "Couldn't read the image"); });
    return () => { cancelled = true; };
  }, [imageFile]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !busy) onCancel?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel, busy]);

  const onCropComplete = useCallback((_area, areaPixels) => {
    areaRef.current = areaPixels;
  }, []);

  const confirm = async () => {
    if (!imageDataUrl || !areaRef.current) return;
    setBusy(true);
    setError(null);
    try {
      const blob = await squareJpeg(imageDataUrl, areaRef.current);
      onCrop?.(new File([blob], "logo.jpg", { type: "image/jpeg" }));
    } catch (err) {
      setError(err.message || "Couldn't prepare the logo");
      setBusy(false);
    }
  };

  if (!imageFile) return null;

  return (
    <div className="hic-backdrop" onClick={() => !busy && onCancel?.()}>
      <div className="hic-modal" onClick={(e) => e.stopPropagation()}>
        <div className="hic-header">
          <h2 className="hic-title">Move and scale</h2>
          <p className="hic-subtitle">Drag and zoom to frame the logo in the circle. The circle is exactly what everyone sees.</p>
        </div>

        <div className="hic-stage">
          {imageDataUrl && (
            <Cropper
              image={imageDataUrl}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              minZoom={1}
              maxZoom={5}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </div>

        <div className="hic-controls">
          <label className="hic-zoom-label">Zoom</label>
          <input
            className="hic-zoom-slider"
            type="range"
            min={1}
            max={5}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            disabled={busy}
          />
        </div>

        {error && <div className="hic-error">{error}</div>}

        <div className="hic-actions">
          <button type="button" className="hic-cancel-btn" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="hic-confirm-btn" onClick={confirm} disabled={busy || !imageDataUrl}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
